import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PORTAL_CONTENT_ACTIVITY_EVENT } from "@/lib/portalContentActivity";

const SESSION_KEY = "pleiades_portal_session";

function sessionId() {
  let value = sessionStorage.getItem(SESSION_KEY);
  if (!value) {
    value = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, value);
  }
  return value;
}

export default function PortalActivityTracker() {
  const { status, user } = useAuth();
  const location = useLocation();
  const lastRoute = useRef(null);
  const currentSection = useRef("home");

  useEffect(() => {
    if (status !== "authed" || user?.role !== "danisan") return;
    const route = location.pathname;
    const tracked = route === "/danisan-paneli"
      || route.startsWith("/ozel-davet/")
      || route.startsWith("/danismanlik");
    if (!tracked || lastRoute.current === route) return;
    lastRoute.current = route;
    api.post("/client-portal/activity", {
      event_type: "page_view",
      route,
      session_id: sessionId(),
    }).catch(() => {});
  }, [location.pathname, status, user?.role]);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "danisan") return;
    const route = location.pathname;
    if (route !== "/danisan-paneli") return;
    const id = sessionId();
    const post = (eventType, action = currentSection.current) => api.post("/client-portal/activity", {
      event_type: eventType,
      route,
      session_id: id,
      action,
    }).catch(() => {});

    post("session_start");
    const onSection = (event) => {
      const action = event?.detail?.section;
      if (!action) return;
      currentSection.current = action;
      post("section_view", action);
    };
    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") post("heartbeat");
    }, 30000);
    const onPageHide = () => post("session_end");
    const onContentActivity = (event) => {
      const detail = event?.detail || {};
      if (!detail.event_type || !detail.content_type || !detail.content_id) return;
      api.post("/client-portal/activity", {
        event_type: detail.event_type,
        route,
        session_id: id,
        action: currentSection.current,
        content_type: detail.content_type,
        content_id: detail.content_id,
        content_title: detail.content_title || undefined,
        duration_seconds: detail.duration_seconds || 0,
      }).catch(() => {});
    };
    window.addEventListener("pleiades:portal-section", onSection);
    window.addEventListener(PORTAL_CONTENT_ACTIVITY_EVENT, onContentActivity);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener("pleiades:portal-section", onSection);
      window.removeEventListener(PORTAL_CONTENT_ACTIVITY_EVENT, onContentActivity);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [location.pathname, status, user?.role]);

  return null;
}
